import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  BsPatchCheckFill,
  BsShieldCheck,
  BsRecycle,
  BsGraphUpArrow,
  BsFillGrid3X3GapFill,
  BsDropletFill,
  BsAwardFill,
  BsActivity,
} from "react-icons/bs";

const Quality = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title =
      "Industrial Quality Assurance & Safety Compliance | NGO Waters";
  }, []);

  // System Protocols Unified Data Grid Array
  const qualityProtocols = [
    {
      icon: <BsPatchCheckFill className="fs-5 text-success" />,
      title: "Mineral Infusion Mapping",
      text: "Systematic dissolution of critical electrolytes matching precise mineral ratios to preserve absolute taste neutralizer variables and metabolic health frameworks.",
    },
    {
      icon: (
        <BsActivity
          className="fs-5"
          style={{ color: "var(--brand-mineral-aqua)" }}
        />
      ),
      title: "Automated Micro Facilities",
      text: "State-of-the-art packaging lines monitored via continuous sensor matrices to enforce closed-loop environmental isolation and strict batch safety rules.",
    },
    {
      icon: <BsRecycle className="fs-5 text-success" />,
      title: "Eco-Conscious Architecture",
      text: "Optimized processing plants built consciously to drastically constrain groundwater reject parameters while maintaining high-yield processing pipelines.",
    },
    {
      icon: (
        <BsShieldCheck
          className="fs-5"
          style={{ color: "var(--brand-copper)" }}
        />
      ),
      title: "Multi-Tier Analytical Labs",
      text: "Every micro-batch undergoes strict hourly chemical evaluation checks ensuring complete absence of heavy physical particles or chemical anomalies.",
    },
  ];

  const operationalFeatures = [
    {
      icon: (
        <BsRecycle
          className="fs-5"
          style={{ color: "var(--brand-mineral-aqua)" }}
        />
      ),
      title: "100% Recyclable Frameworks",
      text: "Utilizing highly structural polymer grades explicitly designed for seamless post-consumer reclamation pathways to prevent persistent material landfills.",
    },
    {
      icon: <BsDropletFill className="fs-5 text-primary" />,
      title: "7-Stage Scientific Purity",
      text: "Passing raw supply lines through reverse osmosis mechanical filters, micro-strain membranes, and intense ultraviolet treatment arrays.",
    },
    {
      icon: (
        <BsAwardFill
          className="fs-5"
          style={{ color: "var(--brand-copper)" }}
        />
      ),
      title: "Regulatory Bio-Safety",
      text: "Engineered and packed completely in strict alignment with FSSAI regulations and national IS 14543 packaging validation parameters.",
    },
    {
      icon: <BsGraphUpArrow className="fs-5 text-danger" />,
      title: "Resource Footprint Audits",
      text: "Continuous validation tracking applied across every automated asset to regularly optimize carbon displacement grids and recycling curves.",
    },
  ];

  const fluidSpring = { type: "spring", stiffness: 45, damping: 15 };

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Subtle Ambient Blobs for Luxury Depth */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "550px",
          height: "550px",
          background: "rgba(54, 191, 216, 0.04)",
          top: "-180px",
          left: "-50px",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.03)",
          bottom: "10%",
          right: "-60px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Structured SEO / GEO / AEO Schema Injection for Production Analytics */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProductComplianceDocument",
          name: "NGO Waters Quality Assurance Protocol",
          productionStandard: "IS 14543 Certified Packaged Mineral Water",
          manufacturer: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
          factoryLocation: {
            "@type": "PostalAddress",
            streetAddress: "Village - Subakara, Thana - Gonda",
            addressLocality: "Aligarh",
            addressRegion: "Uttar Pradesh",
            postalCode: "202123",
            addressCountry: "IN",
          },
          complianceMetrics: [
            "7-Stage Scientific Purification",
            "FSSAI Food Safety Standards Compliance",
            "Eco-Conscious Packaging Waste Management",
          ],
        })}
      </script>

      {/* ================= COMPLIANCE HERO HEADER ================= */}
      <section className="container py-4 position-relative z-1 mb-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(26, 60, 107, 0.08)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.75rem",
              borderRadius: "8px",
            }}
          >
            Zero Compromise Laboratory Standards
          </span>
          <h1
            className="display-5 fw-bold mb-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-1.5px" }}
          >
            Our Quality Blueprint
          </h1>
          <p
            className="text-secondary fs-5 mx-auto mb-0"
            style={{ maxWidth: "650px", lineHeight: "1.65", fontWeight: "400" }}
          >
            Uncompromising technical mechanics engineered to deliver crisp,
            pristine trace minerals while validating strict environmental
            conservation and food-safety parameters.
          </p>
        </motion.div>
      </section>

      {/* ================= CORE QUALITY CRITERIA GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <motion.div
          className="row g-4"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.12 } },
          }}
        >
          {qualityProtocols.map((item, index) => (
            <div className="col-md-6 col-lg-3" key={index}>
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                whileHover={{
                  y: -6,
                  boxShadow: "0px 12px 35px rgba(26, 60, 107, 0.03)",
                }}
                className="card border-0 h-100 bg-white p-4"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.015)",
                      width: "42px",
                      height: "42px",
                      minWidth: "42px",
                    }}
                  >
                    {item.icon}
                  </div>
                  <h5
                    className="fw-bold mb-0 text-dark"
                    style={{ fontSize: "1.05rem", lineHeight: "1.3" }}
                  >
                    {item.title}
                  </h5>
                </div>
                <p
                  className="text-muted small mb-0"
                  style={{ lineHeight: "1.6" }}
                >
                  {item.text}
                </p>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= CENTRAL STATEMENT: WHY NGO WATERS ================= */}
      <section className="container my-5 py-5 position-relative z-1">
        <div className="row justify-content-center">
          <motion.div
            className="col-lg-10 col-xl-8 text-center p-4 p-md-5"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.45)",
              border: "1px solid rgba(0,0,0,0.03)",
              borderRadius: "28px",
              backdropFilter: "blur(10px)",
            }}
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="d-flex justify-content-center mb-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(54, 191, 216, 0.1)",
                  width: "50px",
                  height: "50px",
                }}
              >
                <BsFillGrid3X3GapFill
                  style={{ color: "var(--brand-mineral-aqua)" }}
                />
              </div>
            </div>
            <h2
              className="fw-bold mb-4 fs-3"
              style={{
                color: "var(--brand-deep-blue)",
                letterSpacing: "-0.5px",
              }}
            >
              Why Do Regional Networks Anchor With Us?
            </h2>
            <div
              className="d-flex flex-column gap-3 text-secondary small text-start px-md-3"
              style={{ lineHeight: "1.75" }}
            >
              <p className="mb-0">
                NGO Waters redefines pure industrial supply by maintaining zero
                compromise between physical hydration stability and local
                ethical accountability. Our production facilities—monitored via
                professional infrastructure frameworks under{" "}
                <strong>Ponia Food And Beverage Private Limited</strong>—utilize
                multi-stage isolation filters to remove heavy toxic compounds
                while locking in verified pristine trace mineral attributes.
              </p>
              <p className="mb-0">
                Unlike high-overhead traditional operations that optimize
                commercial margins for corporate distribution channels,{" "}
                <strong>50% of our clean net profits</strong> are legally
                structured to support rural water pipeline configurations,
                deep-well water reclamation, and sanitization setups. We treat
                clean water access as an absolute utility blueprint rather than
                a restricted retail privilege.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= ADDITIONAL INTEGRITY FEATURES GRID ================= */}
      <section className="container my-5 position-relative z-1">
        <motion.div
          className="row g-4"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-30px" }}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } },
          }}
        >
          {operationalFeatures.map((process, index) => (
            <div className="col-sm-6 col-md-4 col-lg-3" key={index}>
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: fluidSpring },
                }}
                whileHover={{
                  y: -6,
                  transition: { duration: 0.25, ease: "easeOut" },
                }}
                className="card h-100 border-0 bg-white"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                }}
              >
                {/* Micro accent top boundary indicator line */}
                <div
                  className="position-absolute top-0 start-0 w-100"
                  style={{
                    height: "3px",
                    backgroundColor: "var(--brand-mineral-aqua)",
                    opacity: 0.4,
                    borderRadius: "24px 24px 0 0",
                  }}
                />

                <div className="card-body p-4 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor: "rgba(0,0,0,0.01)",
                          width: "40px",
                          height: "40px",
                        }}
                      >
                        {process.icon}
                      </div>
                      <span className="text-muted opacity-25 fs-6 fw-bold">
                        #{index + 1}
                      </span>
                    </div>
                    <h5
                      className="fw-bold mb-3 text-dark"
                      style={{ fontSize: "1rem", lineHeight: "1.4" }}
                    >
                      {process.title}
                    </h5>
                    <p
                      className="text-muted small mb-0"
                      style={{ lineHeight: "1.6", fontSize: "0.85rem" }}
                    >
                      {process.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= QUALITY POLICY BANNER CARD ================= */}
      <section className="container my-5 position-relative z-1">
        <div
          className="bg-white border p-4 p-md-5 text-center text-md-start shadow-none"
          style={{
            borderRadius: "24px",
            borderColor: "rgba(0,0,0,0.04)",
            borderLeft: "5px solid var(--brand-copper)",
          }}
        >
          <div className="row align-items-center g-4">
            <div className="col-md-9">
              <h4
                className="fw-bold mb-2 fs-5"
                style={{ color: "var(--brand-deep-blue)" }}
              >
                Looking for Commercial Verification Data?
              </h4>
              <p
                className="text-muted small mb-0"
                style={{ lineHeight: "1.6" }}
              >
                Our micro-chemical lab test logs, water analysis certifications
                (IS 14543 specifications), and legal distribution compliance
                pipelines are fully open for wholesale or bulk enterprise
                review.
              </p>
            </div>
            <div className="col-md-3 text-md-end">
              <a
                href="https://www.cognitoforms.com/HARIOMSINGH1/NGOWatersBulkOrderRequest"
                target="_blank"
                rel="noopener noreferrer"
                className="btn px-4 py-2.5 text-white fw-bold border-0"
                style={{
                  backgroundColor: "var(--brand-deep-blue)",
                  borderRadius: "10px",
                  fontSize: "0.9rem",
                }}
              >
                Request Lab Report
              </a>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Quality;
